import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { adminEmailAttachmentsAPI } from '../services/adminApi';

const MAX_SINGLE_FILE_MB = 7;
const MAX_TOTAL_MB = 9;
const MAX_FILES = 10;

const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg,.zip,.txt,.pptx,.ppt';

const Wrap = styled.div`
  margin-bottom: 1.1rem;
`;
const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.4rem;
`;
const Hint = styled.div`
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.4;
  margin-top: 0.3rem;
`;
const DropZone = styled.div`
  border: 2px dashed ${(p) => (p.$over ? '#3b82f6' : '#d1d5db')};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  background: ${(p) => (p.$over ? '#eff6ff' : '#fafafa')};
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: #93c5fd;
    background: #f0f7ff;
  }
`;
const DropText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  strong {
    color: #3b82f6;
    cursor: pointer;
  }
`;
const FileList = styled.div`
  margin-top: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;
const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.65rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.82rem;
`;
const FileName = styled.span`
  flex: 1;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FileSize = styled.span`
  color: #64748b;
  font-size: 0.75rem;
  white-space: nowrap;
`;
const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0 0.2rem;
  &:hover {
    color: #b91c1c;
  }
`;
const ProgressOuter = styled.div`
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  flex: 1;
  min-width: 60px;
`;
const ProgressInner = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  background: linear-gradient(90deg, #3b82f6, #0ea5e9);
  transition: width 0.15s ease;
`;
const TotalBar = styled.div`
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
`;
const TotalOuter = styled.div`
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;
const TotalInner = styled.div`
  height: 100%;
  width: ${(p) => Math.min(100, p.$pct)}%;
  background: ${(p) => (p.$pct > 90 ? '#ef4444' : '#3b82f6')};
  transition: width 0.2s ease;
`;
const ErrorMsg = styled.div`
  font-size: 0.8rem;
  color: #dc2626;
  margin-top: 0.35rem;
  padding: 0.4rem 0.6rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(filename) {
  const ext = (filename || '').toLowerCase().split('.').pop();
  const icons = {
    pdf: '\uD83D\uDCC4', doc: '\uD83D\uDCC3', docx: '\uD83D\uDCC3',
    xls: '\uD83D\uDCCA', xlsx: '\uD83D\uDCCA', csv: '\uD83D\uDCCA',
    png: '\uD83D\uDDBC\uFE0F', jpg: '\uD83D\uDDBC\uFE0F', jpeg: '\uD83D\uDDBC\uFE0F',
    gif: '\uD83D\uDDBC\uFE0F', webp: '\uD83D\uDDBC\uFE0F', svg: '\uD83D\uDDBC\uFE0F',
    zip: '\uD83D\uDCE6', txt: '\uD83D\uDCC4',
    pptx: '\uD83D\uDCCA', ppt: '\uD83D\uDCCA',
  };
  return icons[ext] || '\uD83D\uDCCE';
}

/**
 * @param {{ disabled?: boolean, sessionId: string|null, onSessionChange: (id: string|null) => void, files: Array, onFilesChange: (files: Array) => void }} props
 */
export default function EmailAttachments({
  disabled = false,
  sessionId,
  onSessionChange,
  files,
  onFilesChange,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState(null);

  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
  const totalPct = (totalSize / (MAX_TOTAL_MB * 1024 * 1024)) * 100;

  const handleUpload = useCallback(
    async (file) => {
      setError(null);
      if (!file) return;

      const ext = (file.name || '').toLowerCase().match(/\.[^.]+$/)?.[0] || '';
      const allowed = ACCEPTED_EXTENSIONS.split(',');
      if (!allowed.includes(ext)) {
        setError(`"${ext}" is not supported. Allowed: ${ACCEPTED_EXTENSIONS}`);
        return;
      }
      if (file.size > MAX_SINGLE_FILE_MB * 1024 * 1024) {
        setError(`"${file.name}" is ${formatBytes(file.size)} — max ${MAX_SINGLE_FILE_MB} MB per file.`);
        return;
      }
      if (files.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} attachments. Remove one first.`);
        return;
      }
      const newTotal = totalSize + file.size;
      if (newTotal > MAX_TOTAL_MB * 1024 * 1024) {
        setError(
          `Adding "${file.name}" (${formatBytes(file.size)}) would exceed the ${MAX_TOTAL_MB} MB total limit (currently ${formatBytes(totalSize)}).`
        );
        return;
      }

      setUploading({ filename: file.name, pct: 0 });

      try {
        const res = await adminEmailAttachmentsAPI.upload(file, sessionId, (evt) => {
          if (evt.total) {
            setUploading((prev) => prev && { ...prev, pct: Math.round((evt.loaded / evt.total) * 100) });
          }
        });
        const data = res?.data?.data || res?.data;
        if (!data?.fileId) throw new Error('Upload returned no fileId');

        if (!sessionId) {
          onSessionChange(data.sessionId);
        }
        onFilesChange([
          ...files,
          { id: data.fileId, filename: data.filename || file.name, size: file.size },
        ]);
      } catch (err) {
        const msg = err?.response?.data?.message || err.message || 'Upload failed';
        setError(msg);
        toast.error(msg);
      } finally {
        setUploading(null);
      }
    },
    [sessionId, files, totalSize, onSessionChange, onFilesChange]
  );

  const handleRemove = useCallback(
    async (fileId) => {
      if (!sessionId) return;
      try {
        await adminEmailAttachmentsAPI.removeFile(sessionId, fileId);
        const next = files.filter((f) => f.id !== fileId);
        onFilesChange(next);
        if (next.length === 0) {
          onSessionChange(null);
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Remove failed');
      }
    },
    [sessionId, files, onFilesChange, onSessionChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleUpload(file);
  };

  return (
    <Wrap>
      <Label>Attachments (optional)</Label>
      <DropZone
        $over={dragOver}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          style={{ display: 'none' }}
          disabled={disabled || !!uploading}
          onChange={onFileInput}
        />
        {uploading ? (
          <FileRow style={{ background: 'transparent', border: 'none', justifyContent: 'center' }}>
            <FileName>{uploading.filename}</FileName>
            <ProgressOuter>
              <ProgressInner $pct={uploading.pct} />
            </ProgressOuter>
            <FileSize>{uploading.pct}%</FileSize>
          </FileRow>
        ) : (
          <DropText>
            Drag &amp; drop a file here, or <strong>browse</strong>
          </DropText>
        )}
      </DropZone>

      <Hint>
        Max {MAX_SINGLE_FILE_MB} MB per file, {MAX_TOTAL_MB} MB total, up to {MAX_FILES} files.
        Supported: PDF, Word, Excel, CSV, images (PNG/JPG/GIF/WebP/SVG), ZIP, TXT, PowerPoint.
      </Hint>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {files.length > 0 && (
        <>
          <FileList>
            {files.map((f) => (
              <FileRow key={f.id}>
                <span>{fileIcon(f.filename)}</span>
                <FileName title={f.filename}>{f.filename}</FileName>
                <FileSize>{formatBytes(f.size)}</FileSize>
                <RemoveBtn
                  type="button"
                  onClick={() => handleRemove(f.id)}
                  disabled={disabled}
                  title="Remove"
                  aria-label={`Remove ${f.filename}`}
                >
                  &times;
                </RemoveBtn>
              </FileRow>
            ))}
          </FileList>
          <TotalBar>
            <span>
              {files.length} file{files.length !== 1 ? 's' : ''} &middot; {formatBytes(totalSize)} / {MAX_TOTAL_MB} MB
            </span>
            <TotalOuter>
              <TotalInner $pct={totalPct} />
            </TotalOuter>
          </TotalBar>
        </>
      )}
    </Wrap>
  );
}
